import React from "react";
import { useI18n } from "../../i18n";
import { LLC, isLLCFormed } from "../../legal";

export default function Terms() {
  const { lang } = useI18n();
  const C = COPY[lang];
  return (
    <div className="container-cd py-16 max-w-3xl" data-testid="legal-terms-page">
      <div className="overline">{C.kicker}</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[0.92]">{C.title}</h1>
      <div className="mt-4 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        {C.effective}: {LLC.legal_effective_date} · {isLLCFormed ? LLC.name : LLC.pending_label}
      </div>

      <div className="mt-10 space-y-10">
        {C.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-display text-2xl md:text-3xl">{s.h}</h2>
            {s.p.map((para, j) => (
              <p key={j} className="mt-3 text-sm md:text-base" style={{ color: "var(--ink-2)" }}>
                {para}
              </p>
            ))}
          </section>
        ))}

        <section>
          <h2 className="font-display text-2xl md:text-3xl">{C.contact}</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
            {LLC.name} · {LLC.registered_address_line_1} · {LLC.registered_address_line_2}
            <br />
            {C.legal_notices}: <a className="underline" href={`mailto:${LLC.contact_email}`}>{LLC.contact_email}</a>
          </p>
        </section>
      </div>
    </div>
  );
}

const COPY = {
  en: {
    kicker: "LEGAL · TERMS OF SERVICE",
    title: "Terms of Service",
    effective: "Effective",
    contact: "Contact",
    legal_notices: "Legal notices",
    sections: [
      {
        h: "1. Acceptance",
        p: [
          "By accessing or using Corridor Digital (the “Service”), you agree to these Terms of Service. If you do not agree, you may not use the Service. These Terms apply to manufacturer accounts, US buyer visitors, and any party interacting with content on the platform.",
        ],
      },
      {
        h: "2. The Service",
        p: [
          "Corridor Digital is a bilingual directory and compliance-document platform for IMMEX manufacturers operating in the Chihuahua–El Paso corridor (Juárez, Parral, Chihuahua City). The Service includes corporate microsites, a document vault with share-link controls, an RFQ inquiry channel, the Cross-Border Data Bridge dashboard, and subscription tiers (Free, Pro, Premium, Enterprise/IMMEX 4.0).",
          "We are an introduction and information platform. We do not act as an agent, broker, or party to any procurement transaction between manufacturers and buyers.",
        ],
      },
      {
        h: "3. Accounts",
        p: [
          "Manufacturer accounts must be operated by an authorized representative of the legal entity. You agree the information you submit (company name, legal name, RFC, IMMEX number, certifications) is accurate and that you may legally publish it. We may suspend any account that publishes false certification data.",
        ],
      },
      {
        h: "4. Subscriptions and payment",
        p: [
          "Paid plans (Pro, Premium, Enterprise) are billed monthly in advance via Stripe. Plans renew automatically until cancelled. Cancellation takes effect at the end of the current billing period. See our Refund Policy for refund eligibility.",
          "Founding-tier pricing (where offered) is locked for the duration of an uninterrupted, paid subscription. A cancellation forfeits founding pricing.",
        ],
      },
      {
        h: "5. Content you publish",
        p: [
          "You retain ownership of profile content (descriptions, images, documents) you upload. You grant Corridor Digital a worldwide, royalty-free license to host, display, translate, cache, and distribute that content for the purpose of operating the Service, including in bilingual format and in our Supplier Digest newsletter.",
          "You are responsible for confidentiality. Documents uploaded to the vault are not visible publicly unless you create a share link. Share links may be set to expire and may be revoked at any time from your dashboard.",
        ],
      },
      {
        h: "6. Verification and badges",
        p: [
          "“Corridor Verified” and “Featured Supplier” badges are awarded by Corridor Digital at our discretion. We may revoke a badge if we receive credible evidence that published information is materially inaccurate.",
        ],
      },
      {
        h: "7. Buyer use",
        p: [
          "US buyers and any party accessing manufacturer profiles agree that information is provided as-is and that procurement decisions, due diligence, audits, and contractual relationships are solely between buyer and manufacturer. Corridor Digital is not a party to any RFQ, quote, or contract.",
        ],
      },
      {
        h: "8. Acceptable use",
        p: [
          "You may not scrape the directory at scale, impersonate manufacturers, upload malware, or use the Service to violate USMCA, IMMEX, US Customs, or Mexican import-export regulations. We may suspend access for any abuse.",
        ],
      },
      {
        h: "9. Disclaimers",
        p: [
          "The Service is provided “AS IS” and “AS AVAILABLE” without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, or non-infringement.",
        ],
      },
      {
        h: "10. Limitation of liability",
        p: [
          "To the maximum extent permitted by law, Corridor Digital’s aggregate liability for any claim relating to the Service shall not exceed the amount paid by you for the Service in the twelve (12) months preceding the claim.",
        ],
      },
      {
        h: "11. Governing law",
        p: [
          "These Terms are governed by the laws of the State of " + (LLC.state) + ", United States, without regard to conflict-of-laws principles. Disputes shall be resolved in the state and federal courts located in that State.",
        ],
      },
      {
        h: "12. Changes",
        p: [
          "We may revise these Terms from time to time. We will post the updated effective date at the top of this page. Material changes will be emailed to active accountholders.",
        ],
      },
    ],
  },
  es: {
    kicker: "LEGAL · TÉRMINOS DEL SERVICIO",
    title: "Términos del Servicio",
    effective: "Vigente desde",
    contact: "Contacto",
    legal_notices: "Avisos legales",
    sections: [
      {
        h: "1. Aceptación",
        p: [
          "Al acceder o utilizar Corridor Digital (el “Servicio”), aceptas estos Términos del Servicio. Si no estás de acuerdo, no puedes usar el Servicio. Estos Términos aplican a cuentas de fabricantes, visitantes compradores en EE.UU. y cualquier parte que interactúe con el contenido de la plataforma.",
        ],
      },
      {
        h: "2. El Servicio",
        p: [
          "Corridor Digital es una plataforma bilingüe de directorio y documentos de cumplimiento para fabricantes IMMEX que operan en el corredor Chihuahua–El Paso (Juárez, Parral, Chihuahua City). El Servicio incluye micrositios corporativos, una bóveda de documentos con controles de enlace, un canal de cotizaciones (RFQ), el panel Cross-Border Data Bridge y niveles de suscripción (Gratis, Pro, Premium, Enterprise/IMMEX 4.0).",
          "Somos una plataforma de presentación e información. No actuamos como agente, intermediario ni parte en ninguna transacción de compra entre fabricantes y compradores.",
        ],
      },
      {
        h: "3. Cuentas",
        p: [
          "Las cuentas de fabricante deben ser operadas por un representante autorizado de la entidad legal. Aceptas que la información que envías (nombre comercial, razón social, RFC, número IMMEX, certificaciones) es exacta y que puedes publicarla legalmente. Podemos suspender cualquier cuenta que publique datos de certificación falsos.",
        ],
      },
      {
        h: "4. Suscripciones y pago",
        p: [
          "Los planes pagados (Pro, Premium, Enterprise) se facturan mensualmente por adelantado vía Stripe. Los planes se renuevan automáticamente hasta su cancelación. La cancelación surte efecto al final del período de facturación actual. Consulta nuestra Política de Reembolsos.",
          "Los precios de nivel Fundador (cuando se ofrecen) se mantienen bloqueados durante la duración de una suscripción pagada e ininterrumpida. La cancelación implica perder el precio Fundador.",
        ],
      },
      {
        h: "5. Contenido que publicas",
        p: [
          "Conservas la propiedad del contenido de tu perfil (descripciones, imágenes, documentos) que subas. Otorgas a Corridor Digital una licencia mundial y libre de regalías para alojar, mostrar, traducir, almacenar en caché y distribuir ese contenido con el fin de operar el Servicio, incluyendo en formato bilingüe y en nuestro boletín Supplier Digest.",
          "Eres responsable de la confidencialidad. Los documentos subidos a la bóveda no son visibles públicamente a menos que crees un enlace de compartir. Los enlaces pueden caducar y pueden ser revocados en cualquier momento desde tu panel.",
        ],
      },
      {
        h: "6. Verificación e insignias",
        p: [
          "Las insignias “Corredor Verificado” y “Proveedor Destacado” se otorgan a discreción de Corridor Digital. Podemos revocar una insignia si recibimos evidencia creíble de que la información publicada es materialmente inexacta.",
        ],
      },
      {
        h: "7. Uso por compradores",
        p: [
          "Los compradores en EE.UU. y cualquier parte que acceda a los perfiles de fabricantes aceptan que la información se proporciona tal cual y que las decisiones de compra, due diligence, auditorías y relaciones contractuales son únicamente entre comprador y fabricante. Corridor Digital no es parte de ninguna RFQ, cotización o contrato.",
        ],
      },
      {
        h: "8. Uso aceptable",
        p: [
          "No puedes hacer scraping del directorio a escala, suplantar fabricantes, subir malware, ni usar el Servicio para violar las regulaciones USMCA, IMMEX, Aduanas EE.UU. o de comercio exterior mexicano. Podemos suspender el acceso por cualquier abuso.",
        ],
      },
      {
        h: "9. Renuncias",
        p: [
          "El Servicio se proporciona “TAL CUAL” y “SEGÚN DISPONIBILIDAD” sin garantías de ningún tipo, expresas o implícitas, incluyendo comerciabilidad, idoneidad para un fin particular o no infracción.",
        ],
      },
      {
        h: "10. Limitación de responsabilidad",
        p: [
          "En la medida máxima permitida por la ley, la responsabilidad agregada de Corridor Digital por cualquier reclamación relacionada con el Servicio no excederá el monto pagado por ti por el Servicio en los doce (12) meses anteriores a la reclamación.",
        ],
      },
      {
        h: "11. Ley aplicable",
        p: [
          "Estos Términos se rigen por las leyes del Estado de " + (LLC.state) + ", Estados Unidos, sin atender a los principios de conflicto de leyes. Las disputas se resolverán en los tribunales estatales y federales ubicados en ese Estado.",
        ],
      },
      {
        h: "12. Cambios",
        p: [
          "Podemos modificar estos Términos periódicamente. Publicaremos la fecha de vigencia actualizada en la parte superior de esta página. Los cambios materiales se enviarán por correo electrónico a los titulares de cuenta activos.",
        ],
      },
    ],
  },
};
